import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  // Switch, // kept for future stats/AI toggle
  Chip,
  Tooltip,
  LinearProgress,
  Pagination,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import axios from "axios";
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import SmartToyIcon from '@mui/icons-material/SmartToy'; // kept for future AI/stats toggle
// import BarChartIcon from '@mui/icons-material/BarChart'; // kept for future AI/stats toggle
import BattleField from "../components/BattleField";
import BattleConditionsDialog from '../components/BattleConditionsDialog';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BuildIcon from '@mui/icons-material/Build';
import HelpDialog from '../components/HelpDialog';
import useDraggable from "../hooks/useDraggable";
import SEO from "../components/SEO";

// Decode base64 import payload from URL ?import= param
function parseImportParam() {
  try {
    const param = new URLSearchParams(window.location.search).get('import');
    if (!param) return null;
    const raw = atob(param);
    const json = new TextDecoder().decode(Uint8Array.from(raw, c => c.charCodeAt(0)));
    const data = JSON.parse(json);
    console.log('[TurnAssistant] Imported battle data:', data);
    return data;
  } catch (e) {
    console.error('[TurnAssistant] Failed to parse import param:', e);
    return null;
  }
}

const WEATHER_NORM = { rain: 'Rain', sun: 'SunnyDay', sunny: 'SunnyDay', sunnyday: 'SunnyDay', sandstorm: 'Sandstorm', snow: 'Hail', hail: 'Hail' };
const FIELD_NORM = { grassyterrain: 'GrassyTerrain', electricterrain: 'ElectricTerrain', mistyterrain: 'MistyTerrain', psychicterrain: 'PsychicTerrain' };
const ROOM_NORM = { trickroom: 'TrickRoom', gravity: 'Gravity', magicroom: 'MagicRoom', wonderroom: 'WonderRoom' };

function normConditionValue(map, val) {
  if (!val || val === 'none' || val === '') return 'none';
  return map[val.toLowerCase().replace(/\s/g, '')] || val;
}

function markActiveAsRevealed(team, activePokemon) {
  const activeNames = new Set((activePokemon || []).map(p => p?.name?.toLowerCase()).filter(Boolean));
  return (team || []).map(member => ({
    ...member,
    revealed: member.revealed || activeNames.has((member?.name || '').toLowerCase()),
  }));
}

// Estado inicial compartido para condiciones de batalla
const defaultBattleConditions = {
  weather: "none",
  weatherDuration: 0,
  field: "none",
  fieldDuration: 0,
  room: "none",
  roomDuration: 0,
  sideEffects: {
    yourSide: { tailwind: false, reflect: false, lightscreen: false, auroraveil: false },
    opponentSide: { tailwind: false, reflect: false, lightscreen: false, auroraveil: false }
  },
  sideEffectsDuration: {
    yourSide: { tailwind: 0, reflect: 0, lightscreen: 0, auroraveil: 0 },
    opponentSide: { tailwind: 0, reflect: 0, lightscreen: 0, auroraveil: 0 }
  },
  entryHazards: {
    yourSide: {},
    opponentSide: {}
  },
  entryHazardsLevel: {
    yourSide: {},
    opponentSide: {}
  },
  entryHazardsDuration: {
    yourSide: {},
    opponentSide: {}
  }
};

const COMBOS_PER_PAGE = 10;

const RANK_COLORS = ['#FFD700', '#C0C0C0', '#CD7F32'];
const RANK_LABELS = ['1st', '2nd', '3rd'];

function winRateColor(wr) {
  return wr > 55 ? '#66bb6a' : wr < 45 ? '#ef5350' : '#ffa726';
}

function deltaColor(d) {
  return d > 0 ? '#66bb6a' : d < 0 ? '#ef5350' : 'rgba(255,255,255,0.4)';
}

function MovePill({ move, target, tera, pokemon }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.75,
      backgroundColor: 'rgba(255,255,255,0.08)',
      borderRadius: 2, px: 1.5, py: 0.5,
      flexWrap: 'wrap',
    }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{move}</Typography>
      {tera && (
        <Chip label="TERA" size="small" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, backgroundColor: '#FFD700', color: '#000' }} />
      )}
      {target && target !== '—' && (
        <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)', fontStyle: 'italic' }}>
          → {target}
        </Typography>
      )}
      {pokemon && (
        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>
          ({pokemon})
        </Typography>
      )}
    </Box>
  );
}

// Parse a reason string into { text, delta } where delta is a 0-1 fraction or null
function parseReason(text) {
  // Match patterns like "(+2.1pp win rate)" or "(-0.8pp penalty)" inside the string
  const m = text.match(/\(([+-]\d+\.?\d*)pp[^)]*\)/);
  if (m) {
    const delta = parseFloat(m[1]) / 100;
    const before = text.slice(0, m.index).trim().replace(/\s*—\s*$/, '').trim();
    const after  = text.slice(m.index + m[0].length).trim();
    const mainText = after ? `${before} ${after}`.trim() : before;
    return { text: mainText, delta };
  }
  return { text, delta: null };
}

function BreakdownRow({ text, delta }) {
  const isWarning = text.toLowerCase().startsWith('warning');
  const isNegative = delta !== null ? delta < -0.001 : isWarning;
  const isPositive = delta !== null && delta > 0.001;

  const icon       = isPositive ? '▲' : isNegative ? '▼' : '●';
  const iconColor  = isPositive ? '#66bb6a' : isNegative ? '#ef5350' : 'rgba(255,255,255,0.35)';
  const deltaColor = isPositive ? '#66bb6a' : '#ef5350';

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.35 }}>
      <Typography sx={{ fontSize: '0.68rem', color: iconColor, flexShrink: 0, mt: 0.2, minWidth: 10 }}>
        {icon}
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.82)', flex: 1, lineHeight: 1.4 }}>
        {text}
      </Typography>
      {delta !== null && (
        <Typography sx={{
          fontSize: '0.78rem', fontWeight: 700,
          color: deltaColor,
          flexShrink: 0, minWidth: 44, textAlign: 'right',
        }}>
          {delta > 0 ? '+' : ''}{(delta * 100).toFixed(1)}%
        </Typography>
      )}
    </Box>
  );
}

function buildFallbackReasons(combo) {
  const r = [];
  const isSpread1 = combo.target1 && combo.target1.includes('&');
  const isSpread2 = combo.target2 && combo.target2.includes('&');
  const isSwitch1 = combo.move1?.toLowerCase().startsWith('switch');
  const isSwitch2 = combo.move2?.toLowerCase().startsWith('switch');
  const isProt1   = combo.move1?.toLowerCase() === 'protect';
  const isProt2   = combo.move2?.toLowerCase() === 'protect';

  if (!isSwitch1 && !isProt1 && isSpread1) r.push(`${combo.move1} hits both opponents`);
  if (!isSwitch2 && !isProt2 && isSpread2) r.push(`${combo.move2} hits both opponents`);
  if (isProt1) r.push(`${combo.pokemon1} uses Protect — scouting safely`);
  if (isProt2) r.push(`${combo.pokemon2} uses Protect — scouting safely`);
  if (!isSwitch1 && !isProt1 && !isSpread1 && combo.move1 && combo.target1) r.push(`${combo.pokemon1} uses ${combo.move1} on ${combo.target1}`);
  if (!isSwitch2 && !isProt2 && !isSpread2 && combo.move2 && combo.target2) r.push(`${combo.pokemon2} uses ${combo.move2} on ${combo.target2}`);
  if (isSwitch1) {
    const swTarget1 = combo.move1?.replace(/^switch(\s+to)?\s+/i, '').trim();
    r.push(swTarget1 ? `Switch ${combo.pokemon1} → ${swTarget1} (unfavorable matchup)` : `Switch ${combo.pokemon1} out (unfavorable matchup)`);
  }
  if (isSwitch2) {
    const swTarget2 = combo.move2?.replace(/^switch(\s+to)?\s+/i, '').trim();
    r.push(swTarget2 ? `Switch ${combo.pokemon2} → ${swTarget2} (unfavorable matchup)` : `Switch ${combo.pokemon2} out (unfavorable matchup)`);
  }
  if (combo.teraRecommended1 && combo.pokemon1) r.push(`Tera recommended for ${combo.pokemon1}`);
  if (combo.teraRecommended2 && combo.pokemon2) r.push(`Tera recommended for ${combo.pokemon2}`);
  return r;
}

function ComboCard({ combo, rank, isTop }) {
  const [open, setOpen] = useState(false);
  const winRate  = combo.winRate ?? (combo.score != null ? combo.score * 100 : 0);
  const deltaVal = combo.delta ?? 0;
  const rankColor = RANK_COLORS[rank] ?? 'rgba(255,255,255,0.3)';

  const rawReasons    = (combo.reasons && combo.reasons.length > 0)
    ? combo.reasons
    : buildFallbackReasons(combo);
  const parsedReasons = rawReasons.map(parseReason);
  const hasReasons    = parsedReasons.length > 0;
  const hasQuantified = parsedReasons.some(r => r.delta !== null);

  return (
    <Box
      onClick={() => hasReasons && setOpen(o => !o)}
      sx={{
        borderRadius: 3,
        border: isTop
          ? `2px solid ${rankColor}`
          : '1px solid rgba(255,255,255,0.1)',
        backgroundColor: isTop
          ? 'rgba(255,255,255,0.06)'
          : 'rgba(255,255,255,0.03)',
        mb: 1.5,
        overflow: 'hidden',
        cursor: hasReasons ? 'pointer' : 'default',
        transition: 'background-color 0.15s',
        '&:hover': hasReasons ? { backgroundColor: 'rgba(255,255,255,0.08)' } : {},
      }}
    >
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, flexWrap: 'wrap' }}>
        {/* Rank badge */}
        <Box sx={{
          minWidth: 36, textAlign: 'center',
          fontWeight: 800, fontSize: '0.75rem',
          color: rankColor,
          flexShrink: 0,
        }}>
          {RANK_LABELS[rank] ?? `#${rank + 1}`}
        </Box>

        {/* Move pills */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flex: 1 }}>
          <MovePill
            move={combo.move1}
            target={combo.target1}
            tera={combo.teraRecommended1}
            pokemon={combo.pokemon1}
          />
          {combo.move2 && combo.move2 !== 'Pass' && (
            <>
              <Typography sx={{ alignSelf: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem' }}>+</Typography>
              <MovePill
                move={combo.move2}
                target={combo.target2}
                tera={combo.teraRecommended2}
                pokemon={combo.pokemon2}
              />
            </>
          )}
        </Box>

        {/* Win rate + delta */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, ml: 'auto' }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: winRateColor(winRate) }}>
            {winRate.toFixed(1)}%
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: deltaColor(deltaVal), minWidth: 44, textAlign: 'right' }}>
            {deltaVal > 0 ? '+' : ''}{(deltaVal * 100).toFixed(1)}%
          </Typography>
          {hasReasons && (
            <ExpandMoreIcon sx={{
              color: 'rgba(255,255,255,0.4)',
              fontSize: '1.1rem',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }} />
          )}
        </Box>
      </Box>

      {/* Expandable breakdown */}
      {hasReasons && (
        <Collapse in={open}>
          <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.08)', px: 2, py: 1.25 }}>
            <Typography sx={{
              fontSize: '0.62rem', fontWeight: 600, letterSpacing: 0.5,
              textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', mb: 1,
            }}>
              Score breakdown
            </Typography>
            {parsedReasons.map((r, i) => (
              <BreakdownRow key={i} text={r.text} delta={r.delta} />
            ))}
            {hasQuantified && (
              <>
                <Box sx={{ height: 1, bgcolor: 'rgba(255,255,255,0.08)', my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)' }}>
                    Net effect vs baseline
                  </Typography>
                  <Typography sx={{
                    fontSize: '0.85rem', fontWeight: 700,
                    color: deltaColor(deltaVal),
                  }}>
                    {deltaVal > 0 ? '+' : ''}{(deltaVal * 100).toFixed(1)}%
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

function ComboCards({ combos, page, onPageChange }) {
  const pageCount = Math.ceil(combos.length / COMBOS_PER_PAGE);
  const offset    = (page - 1) * COMBOS_PER_PAGE;
  const visible   = combos.slice(offset, offset + COMBOS_PER_PAGE);
  return (
    <>
      {visible.map((combo, i) => (
        <ComboCard key={i} combo={combo} rank={offset + i} isTop={offset + i < 3} />
      ))}
      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={pageCount} page={page}
            onChange={(_, v) => onPageChange(v)}
            color="primary"
            sx={{ '& .MuiPaginationItem-root': { color: 'white' }, '& .Mui-selected': { backgroundColor: 'rgba(255,255,255,0.2)' } }}
          />
        </Box>
      )}
    </>
  );
}

function DraggablePaperComponent(props) {
  const { ref, style, handleMouseDown } = useDraggable({ resetOnClose: true, handleSelector: '#draggable-dialog-title' });
  return <Paper {...props} ref={ref} style={{ ...props.style, ...style }} onMouseDown={handleMouseDown} />;
}

function TurnAssistantPage() {
  // Parse ?import= once on mount
  const importedData = useMemo(() => parseImportParam(), []);

  const [selectedPokemon, setSelectedPokemon] = useState(
    importedData?.pokemonData || { topLeft: null, topRight: null, bottomLeft: null, bottomRight: null }
  );
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [error, setError] = useState(null);

  // States for format selection
  const [formats, setFormats] = useState([]);
  const [selectedFormat, setSelectedFormat] = useState('');
  const [isLoadingFormats, setIsLoadingFormats] = useState(false);
  const [pokemonList, setPokemonList] = useState([]);

  // Estado para el dialog de Battle Conditions
  const [battleDialogOpen, setBattleDialogOpen] = useState(false);

  // Estado extendido para condiciones de batalla, incluyendo duraciones
  const [battleConditions, setBattleConditions] = useState(() => {
    if (!importedData?.battleConditions) return defaultBattleConditions;
    const bc = importedData.battleConditions;
    return {
      ...defaultBattleConditions,
      weather: normConditionValue(WEATHER_NORM, bc.weather),
      weatherDuration: bc.weatherDuration || 0,
      field: normConditionValue(FIELD_NORM, bc.field),
      fieldDuration: bc.fieldDuration || 0,
      room: normConditionValue(ROOM_NORM, bc.room),
      roomDuration: bc.roomDuration || 0,
      sideEffects: {
        yourSide: { ...defaultBattleConditions.sideEffects.yourSide, ...(bc.sideEffects?.yourSide || {}) },
        opponentSide: { ...defaultBattleConditions.sideEffects.opponentSide, ...(bc.sideEffects?.opponentSide || {}) },
      },
      sideEffectsDuration: {
        yourSide: { ...defaultBattleConditions.sideEffectsDuration.yourSide, ...(bc.sideEffectsDuration?.yourSide || {}) },
        opponentSide: { ...defaultBattleConditions.sideEffectsDuration.opponentSide, ...(bc.sideEffectsDuration?.opponentSide || {}) },
      },
    };
  });

  // Tracks whether import data has been cleared by a reset
  const [importCleared, setImportCleared] = useState(false);

  // Agrega un estado para el diálogo de confirmación
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // Agrega un estado para reiniciar el componente
  const [resetKey, setResetKey] = useState(0);

  // Agrega estados para los equipos (pre-filled from import if available)
  const [yourTeam, setyourTeam] = useState(() =>
    importedData?.yourTeam
      ? markActiveAsRevealed(importedData.yourTeam, [importedData.pokemonData?.topLeft, importedData.pokemonData?.topRight])
      : []
  );
  const [opponentTeam, setopponentTeam] = useState(() =>
    importedData?.opponentTeam
      ? markActiveAsRevealed(importedData.opponentTeam, [importedData.pokemonData?.bottomLeft, importedData.pokemonData?.bottomRight])
      : []
  );

  // Agrega estado para el diálogo de selección de equipo

  // Agrega estado para el diálogo de ayuda
  const [helpOpen, setHelpOpen] = useState(false);

  // Mecánicas del juego: controla qué mecánicas están activas
  const [mechanicsOpen, setMechanicsOpen] = useState(false);
  const [teraEnabled, setTeraEnabled] = useState(false);

  // Pagination for Best Combinations
  const [comboPage, setComboPage] = useState(1);

  // Unified combo list normalized for ComboCard: { move1, move2, target1, target2,
  //   teraRecommended1, teraRecommended2, pokemon1, pokemon2, winRate, delta, reasons }
  const allCombos = useMemo(() => {
    if (!analysisResults) return [];
    const baseWinRate = analysisResults.winRate ?? 50;

    if (analysisResults.mlPowered) {
      const combinations = analysisResults.combinations || [];
      if (!combinations.length) return [];
      const MIN_SHOWN = 5;
      // Data is already normalized by the Node.js backend (move1/2, winRate, teraRecommended1/2…)
      const sorted = [...combinations].sort((a, b) => b.delta - a.delta);
      const positive = sorted.filter(c => c.delta >= 0);
      return positive.length >= MIN_SHOWN ? positive : sorted.slice(0, MIN_SHOWN);
    }

    // Stats mode: already has move1/move2/winRate
    return (analysisResults.topCombinations || [])
      .map(c => ({ ...c, delta: (c.winRate - baseWinRate) / 100, reasons: [] }))
      .sort((a, b) => b.winRate - a.winRate);
  }, [analysisResults]);

  // ML mode toggle (true = AI, false = BigQuery stats) — AI is always on; toggle kept for future use
  const [mlMode] = useState(true);
  // const [mlAvailable, setMlAvailable] = useState(null);
  // useEffect(() => {
  //   axios.get('/api/turn-assistant-ml/health')
  //     .then(res => setMlAvailable(res.data?.mlService === 'connected'))
  //     .catch(() => setMlAvailable(false));
  // }, []);

  // Función para resetear todos los campos (sin resetear el formato)
  const handleResetData = () => {
    setSelectedPokemon({ topLeft: null, topRight: null, bottomLeft: null, bottomRight: null });
    setBattleConditions(defaultBattleConditions);
    setyourTeam([]);
    setopponentTeam([]);
    setAnalysisResults(null);
    setError(null);
    setImportCleared(true);
    setResetDialogOpen(false);
    setTeraEnabled(false);
    // Incrementa la clave para forzar re-montaje de BattleField
    setResetKey(prev => prev + 1);
  };

  const handlePokemonSelect = (pokemonData) => {
    setSelectedPokemon(pokemonData);
    setAnalysisResults(null);
    setError(null);
  };

  const handleAnalyze = async () => {
    // Comprueba que se hayan seleccionado los 4 Pokémon principales
    const allSelected = 
      selectedPokemon.topLeft && 
      selectedPokemon.topRight && 
      selectedPokemon.bottomLeft && 
      selectedPokemon.bottomRight;
    if (!allSelected) {
      setError("Please select all four Pokémon before analyzing.");
      return;
    }
    
    // Comprobación de duplicados en el escenario (ya se hace para cada lado)
    if (selectedPokemon.topLeft.name === selectedPokemon.topRight.name) {
      setError("You cannot use the same Pokémon twice on your team. Please select different Pokémon.");
      return;
    }
    if (selectedPokemon.bottomLeft.name === selectedPokemon.bottomRight.name) {
      setError("Your opponent cannot use the same Pokémon twice. Please select different Pokémon for the opponent.");
      return;
    }

    const validOpponentTeam = opponentTeam.filter(p => p && p.name);
    if (validOpponentTeam.length === 6) {
      const normalize = (str) => str.toLowerCase().trim().replace(/-/g, ' ');
      const opponentTeamNames = validOpponentTeam.map(p => normalize(p.name));
      if (
        !opponentTeamNames.includes(normalize(selectedPokemon.bottomLeft.name)) ||
        !opponentTeamNames.includes(normalize(selectedPokemon.bottomRight.name))
      ) {
        setError("Both active Pokémon (bottomLeft and bottomRight) must be part of the opponent's team.");
        return;
      }
    }
    
    // Verificación mejorada para equipos completos
    const validYourTeam = yourTeam.filter(p => p && p.name);
    if (validYourTeam.length === 6) {
      // Verificar que topLeft está en el equipo completo
      const yourPokemon1 = validYourTeam.find(p => p.name === selectedPokemon.topLeft.name);
      if (!yourPokemon1) {
        setError(`${selectedPokemon.topLeft.name} must be part of your team.`);
        return;
      }
      
      // Verificar que topRight está en el equipo completo
      if (!validYourTeam.find(p => p.name === selectedPokemon.topRight.name)) {
        setError(`${selectedPokemon.topRight.name} must be part of your team.`);
        return;
      }
    }

    // Verificación para el equipo del oponente
    if (opponentTeam && opponentTeam.length === 6) {
      if (!opponentTeam.find(p => p.name === selectedPokemon.bottomLeft.name)) {
        setError(`${selectedPokemon.bottomLeft.name} must be part of opponent's team.`);
        return;
      }
      if (!opponentTeam.find(p => p.name === selectedPokemon.bottomRight.name)) {
        setError(`${selectedPokemon.bottomRight.name} must be part of opponent's team.`);
        return;
      }
    }
    
    setAnalyzing(true);
    setError(null);

    try {
      const endpoint = mlMode
        ? "/api/turn-assistant-ml/analyze"
        : "/api/turn-assistant/analyze";

      const VOLATILE_FIELDS = ['name', 'volatileStatuses', 'stats', 'filterStats'];
      const stripSlot = slot => slot
        ? Object.fromEntries(Object.entries(slot).filter(([k]) => VOLATILE_FIELDS.includes(k)))
        : slot;
      const cleanPokemonData = Object.fromEntries(
        Object.entries(selectedPokemon).map(([slot, data]) => [slot, stripSlot(data)])
      );

      const stripTera = team => (team || []).map(p => p ? {
        ...p,
        teraType: teraEnabled ? p.teraType : '',
        tera_type: teraEnabled ? p.tera_type : '',
        teraActive: teraEnabled ? p.teraActive : false,
        tera_active: teraEnabled ? p.tera_active : false,
      } : p);

      const response = await axios.post(endpoint, {
        pokemonData: cleanPokemonData,
        battleConditions: battleConditions,
        yourTeam: stripTera(yourTeam),
        opponentTeam: stripTera(opponentTeam),
        teraEnabled,
      });

      if (response.data) {
        const data = response.data.data || {};
        setComboPage(1);
        setAnalysisResults({
          matchingGames: response.data.matchingScenarios ?? 0,
          winRate: data.winRate || 0,
          allMoveOptions: data.allMoveOptions || {},
          topCombinations: data.topCombinations || [],
          // ML-specific fields
          mlPowered: data.mlPowered || false,
          evaluation: data.evaluation,
          confidence: data.confidence,
          modelType: data.modelType,
          inferenceTimeMs: data.inferenceTimeMs,
          combinations: data.combinations || [],
        });
      } else {
        setError("No analysis data returned from server.");
      }
    } catch (error) {
      console.error("Error analyzing scenarios:", error);
      if (error.response?.status === 503) {
        setError("AI service is unavailable.");
      } else if (error.response?.data) {
        setError(error.response.data.error || "Error analyzing scenarios.");
      } else {
        setError("Error connecting to server. Please try again later.");
      }
    } finally {
      setAnalyzing(false);
    }
  };

  // Fetch available formats when component mounts
  useEffect(() => {
    const fetchFormatsAndPokemon = async () => {
      try {
        // Get the most recent month first
        const monthsResponse = await axios.get('/api/months');
        if (!monthsResponse.data || monthsResponse.data.length === 0) {
          console.error('No months available');
          return;
        }
        
        const currentYear = new Date().getFullYear().toString();
        const recentMonths = monthsResponse.data.filter(month => month.startsWith(`${currentYear}-`));
        const latestMonth = (recentMonths.length > 0 ? recentMonths : monthsResponse.data)[0];

        // Fetch formats for the latest month
        setIsLoadingFormats(true);
        const formatsResponse = await axios.get(`/api/formats/${latestMonth}`);

        if (formatsResponse.data && formatsResponse.data.length > 0) {
          // Filter for current year VGC formats, excluding BO3
          const currentYearVgcFormats = formatsResponse.data.filter(format => {
            const isVgc = format.toLowerCase().includes('vgc');
            const isCurrent = format.toLowerCase().includes(currentYear);
            const isNotBo3 = !format.toLowerCase().includes('bo3');
            
            return isVgc && isCurrent && isNotBo3;
          });
          
          // Map to store formats with display name and API name
          const formatMap = new Map();
          
          // Process each format to remove any duplicate formats with different ratings
          currentYearVgcFormats.forEach(format => {
            // Get the base format name without rating suffix
            const baseName = format.replace(/-\d+$/, '');
            
            // Store in map to eliminate duplicates
            formatMap.set(baseName, {
              displayName: baseName,
              apiName: format
            });
          });
          
          // Convert map values to array for state
          const cleanedFormats = Array.from(formatMap.values());
          setFormats(cleanedFormats);
          
          // Select first format and fetch its Pokémon
          if (cleanedFormats.length > 0) {
            setSelectedFormat(cleanedFormats[0].apiName);
            fetchPokemonList(latestMonth, cleanedFormats[0].apiName);
          }
        }
      } catch (error) {
        console.error('Error fetching formats:', error);
        setError('Failed to fetch available formats. Please try again later.');
      } finally {
        setIsLoadingFormats(false);
      }
    };
    
    fetchFormatsAndPokemon();
  }, []);
  
  // Handle format change
  const handleFormatChange = (event) => {
    const newFormat = event.target.value;
    setSelectedFormat(newFormat);
    setError(null);
    
    // Need to fetch the latest month again since we're not storing it
    const fetchLatestMonth = async () => {
      try {
        const monthsResponse = await axios.get('/api/months');
        if (monthsResponse.data && monthsResponse.data.length > 0) {
          const currentYear = new Date().getFullYear().toString();
          const recentMonths = monthsResponse.data.filter(month => month.startsWith(`${currentYear}-`));
          const latestMonth = (recentMonths.length > 0 ? recentMonths : monthsResponse.data)[0];
          fetchPokemonList(latestMonth, newFormat);
        }
      } catch (error) {
        console.error('Error fetching latest month:', error);
        setError('Failed to fetch Pokémon list for the selected format.');
      }
    };
    
    fetchLatestMonth();
  };
  
  // Fetch Pokémon list for the selected format
  const fetchPokemonList = async (month, format) => {
    try {
      const response = await axios.get("/api/rankings", {
        params: { month, format }
      });
      
      if (response.data && response.data.data) {
        const pokemonNames = Object.keys(response.data.data);
        setPokemonList(pokemonNames);
      }
    } catch (error) {
      console.error('Error fetching Pokémon list:', error);
      setError('Failed to fetch Pokémon list. Please try again later.');
    }
  };




  return (
    <Box
      key={resetKey}
      component="main"
      role="main"
      aria-label="Turn Assistant"
      sx={{ padding: 3 }}
    >
      <SEO 
        title="Turn Assistant - Battle Strategy Tool"
        description="Get real-time battle assistance for competitive Pokémon VGC. Analyze matchups, predict moves, and make optimal decisions during battles."
        keywords="battle assistant, vgc calculator, pokemon battle tool, turn prediction, competitive assistant, battle strategy"
      />
      <SEO 
        title="Turn Assistant - Battle Strategy Tool"
        description="Get real-time battle assistance for competitive Pokémon VGC. Analyze matchups, predict moves, and make optimal decisions during battles."
        keywords="battle assistant, vgc calculator, pokemon battle tool, turn prediction, competitive assistant, battle strategy"
      />
      <Typography component="h1" variant="h4" gutterBottom>
        Turn Assistant
      </Typography>
      <Typography component="p" variant="subtitle1" sx={{ mb: 1 }}>
        Your AI-powered VGC battle advisor
      </Typography>
      <Typography component="p" variant="body2" sx={{ mb: 0.5, color: 'text.primary' }}>
        Select your active Pokémon and get instant win probability and move recommendations based on a model trained on thousands of competitive battles.
      </Typography>
      <Typography component="p" variant="body2" sx={{ mb: 2, color: 'text.primary'}}>
        Only the 4 active Pokémon are required — but the more you fill in (items, abilities, Tera, moves, team, battle conditions), the more precise the AI analysis.
      </Typography>
      <Typography component="p" variant="body2" sx={{ mb: 2, color: 'text.primary', fontStyle: 'italic' }}>
        Currently in beta — results may contain errors or inaccuracies.
      </Typography>
      
      {isLoadingFormats ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Solo se usa un BattleField */}
          <BattleField
            onPokemonSelect={handlePokemonSelect}
            onTeamSelectYour={(selectedTeam) => {
              console.log("Your team selected (from BattleField):", selectedTeam);
              setyourTeam(selectedTeam);
            }}
            onTeamSelectOpponent={(selectedTeam) => {
              console.log("Opponent team selected (from BattleField):", selectedTeam);
              setopponentTeam(selectedTeam);
            }}
            pokemonList={pokemonList}
            initialSelectedPokemon={!importCleared ? importedData?.pokemonData : undefined}
            initialYourTeam={yourTeam.length > 0 ? yourTeam : undefined}
            initialOpponentTeam={opponentTeam.length > 0 ? opponentTeam : undefined}
            teraEnabled={teraEnabled}
          />

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
            <Button
              variant="contained"
              onClick={() => setBattleDialogOpen(true)}
              startIcon={<SettingsIcon />}
              sx={{ mb: 1, width: '200px', fontSize: '0.875rem', marginTop: '1rem' }}
            >
              Battle Conditions
            </Button>

            <Button
              variant="contained"
              onClick={() => setMechanicsOpen(true)}
              startIcon={<BuildIcon />}
              sx={{ mb: 1, width: '200px', fontSize: '0.875rem', backgroundColor: teraEnabled ? undefined : '#7a7a7a' }}
            >
              Mechanics{!teraEnabled ? ' (Tera OFF)' : ''}
            </Button>

            {/* Mode toggle — hidden; AI is always active */}

            <Button
              variant="containedSuccess"
              color="primary"
              onClick={handleAnalyze}
              disabled={analyzing}
              startIcon={analyzing ? <CircularProgress size={20} /> : <PlayArrowIcon />}
              sx={{ py: 1, px: 4 }}
            >
              {analyzing ? "Analyzing..." : "Analyze Battle"}
            </Button>
            <Button
              variant="outlined"
              color="info"
              onClick={() => setHelpOpen(true)}
              startIcon={<HelpOutlineIcon />}
              sx={{ py: 1, px: 4, mt: 1 }}
            >
              Help
            </Button>
            <Button
              variant="containedCancel"
              color="error"
              onClick={() => setResetDialogOpen(true)}
              startIcon={<DeleteOutlineIcon />}
              sx={{ py: 1, px: 4, mt: 1 }}
            >
              Reset All
            </Button>

            {error && (
              <Alert 
                severity="error" 
                sx={{
                  mt: 2,
                  width: '100%',
                  maxWidth: '400px',
                  textAlign: 'center',
                  backgroundColor: '#E9A5A5',
                  color: '#000000'
                }}
              >
                {error}
              </Alert>
            )}
          </Box>

          <BattleConditionsDialog
            open={battleDialogOpen}
            onClose={() => setBattleDialogOpen(false)}
            battleConditions={battleConditions}
            setBattleConditions={setBattleConditions}
          />

          {/* Dialog de Mecánicas */}
          <Dialog
            open={mechanicsOpen}
            onClose={() => setMechanicsOpen(false)}
            PaperComponent={DraggablePaperComponent}
            PaperProps={{ sx: { minWidth: 320 } }}
          >
            <DialogTitle style={{ cursor: 'grab' }} id="draggable-dialog-title">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BuildIcon fontSize="small" />
                <Typography variant="h6">Mechanics</Typography>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Toggle game mechanics that are active in this battle. Disabling a mechanic removes it from the AI analysis and hides its options in the team editor.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={teraEnabled}
                    onChange={(e) => setTeraEnabled(e.target.checked)}
                    sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>Terastallization</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                      {teraEnabled
                        ? 'Tera is active — type selection and Tera Active options are enabled in team editor, and the AI considers Tera in its predictions.'
                        : 'Tera is disabled — Tera options are hidden in the team editor and the AI ignores Tera entirely.'}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 1 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setMechanicsOpen(false)} variant="contained" color="primary">
                Done
              </Button>
            </DialogActions>
          </Dialog>

          {/* Diálogo de confirmación para resetear */}
          <Dialog
            open={resetDialogOpen}
            onClose={() => setResetDialogOpen(false)}
            PaperComponent={DraggablePaperComponent}
          >
            <DialogTitle style={{ cursor: 'grab' }} id="draggable-dialog-title">
              Confirm Reset
            </DialogTitle>
            <DialogContent>
              <Typography>
                Are you sure you want to reset? This will clear all selected Pokémon, items, abilities, battle conditions, etc.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setResetDialogOpen(false)} variant="outlined" color="primary">
                Cancel
              </Button>
              <Button onClick={handleResetData} variant="contained" color="error">
                Reset
              </Button>
            </DialogActions>
          </Dialog>


          {/* Diálogo de ayuda */}
          <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
        </>
      )}
      
      {/* Analysis Results */}
      {analysisResults && (
        <Paper sx={{ mt: 5, p: 3, backgroundColor: '#221FC7' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography component="h1" variant="h5">
              Analysis Results
            </Typography>
          </Box>

          {/* Win probability bar (ML) */}
          {analysisResults.mlPowered && analysisResults.evaluation != null && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  Your win probability
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {(analysisResults.evaluation * 100).toFixed(1)}%
                  </Typography>
                  {analysisResults.confidence != null && (
                    <Tooltip title={`Model confidence: ${(analysisResults.confidence * 100).toFixed(0)}%`}>
                      <Chip
                        label={`${(analysisResults.confidence * 100).toFixed(0)}% conf.`}
                        size="small"
                        sx={{
                          backgroundColor: analysisResults.confidence > 0.6 ? '#2e7d32' : analysisResults.confidence > 0.35 ? '#e65100' : '#b71c1c',
                          color: 'white',
                          fontSize: '0.7rem',
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              </Box>
              <LinearProgress
                variant="determinate"
                value={analysisResults.evaluation * 100}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 5,
                    backgroundColor: analysisResults.evaluation > 0.6 ? '#66bb6a' : analysisResults.evaluation < 0.4 ? '#ef5350' : '#ffa726',
                  },
                }}
              />
            </Box>
          )}

          {/* Stats mode summary */}
          {!analysisResults.mlPowered && (
            <Typography variant="body1" paragraph>
              {analysisResults.matchingGames > 0
                ? `Based on ${analysisResults.matchingGames} similar scenarios, your win rate is approximately ${analysisResults.winRate.toFixed(1)}%.`
                : 'No matching scenarios found in the database for this exact setup.'}
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Best Move Combinations — expandable cards */}
          {allCombos.length > 0 && (
            <>
              <Typography component="p" variant="h6" gutterBottom>
                Best Move Combinations
                <Typography component="span" variant="body2" sx={{ ml: 1, color: 'rgba(255,255,255,0.6)' }}>
                  ({allCombos.length} total — click a card to see why)
                </Typography>
              </Typography>
              <ComboCards combos={allCombos} page={comboPage} onPageChange={setComboPage} />
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}

export default TurnAssistantPage;
